import {
  ArrowRightOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FBOHeaderBar from "../../../components/element/FBOHeaderBar";
import NavigationBar from "../../../components/element/NavigationBar";
import SnackbarAlert from "../../../components/element/SnackbarAlert";
import { useFormData } from "../../../helpers/FormDataProvider";
import useFBOApi from "../../../services/api/useFBOApi";
import { getServiceUrls } from "../../../services/serviceUrls";
import {
  DATE_FORMAT,
  PAGINATION_PAGE_SIZE,
  PAYMENT_CONSTANTS,
  PAYMENT_DIALOG,
  PAYMENT_FORM_FIELDS,
  PAYMENT_FORM_VALUES,
  CURRENCY_CODES,
  EMPTY_STRING,
} from "../../../utils/constants";
import ROUTE_PATHS from "../../../utils/routePaths";
import { getUrls } from "../../../utils/urls";
import "./FboPaymentScreen.css";
import PaymentDueTable from "./Tables/PaymentDueTable";
import FboFilters from "./View/FBOFilters";
import { MobileDatePicker } from "@mui/x-date-pickers";
import DateLocalizationProvider from "../../../components/element/DateLocalizationProvider";
import dayjs from "dayjs";

const FboPaymentScreen = () => {
  const SERVICE_URL = getServiceUrls();
  const { userData } = useFormData();
  const { fetchFBOUnpaidInvoices, fetchBankConfig, submitWireTransferPayment } =
    useFBOApi();
  const [currentPage, setCurrentPage] = useState(() => {
    return sessionStorage.getItem("fboPaymentCurrentPage")
      ? Number(sessionStorage.getItem("fboPaymentCurrentPage"))
      : 0;
  });
  const pageSize = PAGINATION_PAGE_SIZE;
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [openWireReferenceDialog, setOpenWireReferenceDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    PAYMENT_CONSTANTS.DEFAULT_PAYMENT_TYPE
  );
  const [wireReference, setWireReference] = useState(EMPTY_STRING);
  const [wirePaymentAmount, setWirePaymentAmount] = useState(0);
  const [wirePaymentDate, setWirePaymentDate] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: EMPTY_STRING,
  });
  const [selectedBank, setSelectedBank] = useState(CURRENCY_CODES.BSD);
  const [openBankDetailsDialog, setOpenBankDetailsDialog] = useState(false);

  // Add filter state
  const [filters, setFilters] = useState({
    invoiceNumber: EMPTY_STRING,
    invoiceType: EMPTY_STRING,
  });

  const queryClient = useQueryClient();

  const {
    data: paymentDueData,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["fbo-unpaid-invoices", currentPage, filters],
    queryFn: () => {
      const params = {
        page: currentPage,
        size: pageSize,
      };

      // Add filter parameters if they exist
      if (filters.invoiceNumber) {
        params.invoiceNumber = filters.invoiceNumber;
      }
      if (filters.invoiceType) {
        params.invoiceType = filters.invoiceType;
      }

      return fetchFBOUnpaidInvoices(params);
    },
    retry: false,
  });
  const { data: bankConfig, isLoading: bankConfigLoading } = useQuery({
    queryKey: ["fbo-bank-config"],
    queryFn: () => fetchBankConfig(SERVICE_URL.REACT_APP_PROFILE_ENV),
  });

  useEffect(() => {
    sessionStorage.setItem("fboPaymentCurrentPage", currentPage);
  }, [currentPage]);

  const handleNextPage = () => {
    if (currentPage + 1 < paymentDueData?.data?.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const indexStartFrom = currentPage * pageSize;
  const handlePayClick = () => {
    if (selectedInvoices.length === 0) {
      setSnackbar({ open: false, message: EMPTY_STRING });
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: "No Invoice is selected",
        });
      }, 100);
      return;
    }
    setOpenPaymentDialog(true);
  };
  const getSelectedInvoiceNumbers = () => {
    return selectedInvoices
      .map((invoice) => invoice.dynamicsInvoiceNumber)
      .join(",");
  };

  const calculateSelectedTotal = () => {
    if (!selectedInvoices.length) {
      return 0;
    }
    return selectedInvoices.reduce((total, invoice) => {
      return total + ((parseFloat(invoice.totalAmount) || 0) - (parseFloat(invoice.totalPaymentReceived) || 0));
    }, 0);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
  };
  const handlePaymentSubmit = () => {
    if (selectedPaymentType === PAYMENT_CONSTANTS.PAYMENT_TYPES.WIRE) {
      setOpenPaymentDialog(false);
      setOpenBankDetailsDialog(true);
    } else {
      setOpenPaymentDialog(false);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = getUrls().PAYMENT_URL;
      form.target = "_blank";
      form.style.display = "none";

      const fields = {
        [PAYMENT_FORM_FIELDS.GATEWAY_ACCOUNT]:
          getUrls().PAYMENT_GATEWAY_ACCOUNT,
        [PAYMENT_FORM_FIELDS.AMOUNT]: calculateSelectedTotal().toFixed(2),
        [PAYMENT_FORM_FIELDS.CURRENCY]: PAYMENT_CONSTANTS.CURRENCY,
        [PAYMENT_FORM_FIELDS.CUSTOM_NAME_1]: PAYMENT_FORM_VALUES.CUSTOM_NAME_1,
        [PAYMENT_FORM_FIELDS.CUSTOM_VALUE_1]: getSelectedInvoiceNumbers(),
        [PAYMENT_FORM_FIELDS.CUSTOM_NAME_2]: PAYMENT_FORM_VALUES.CUSTOM_NAME_2,
        [PAYMENT_FORM_FIELDS.CUSTOM_VALUE_2]:
          userData?.associations?.[0]?.fboCustomer?.dynamicsCustomerNumber,
        [PAYMENT_FORM_FIELDS.PAYMENT_TYPE]: PAYMENT_FORM_VALUES.PAYMENT_TYPE,
        [PAYMENT_FORM_FIELDS.BILLING_COUNTRY]: PAYMENT_CONSTANTS.COUNTRY,
        [PAYMENT_FORM_FIELDS.RESPONSE_URL]: getUrls().PAYMENT_RESPONSE_URL,
        [PAYMENT_FORM_FIELDS.EMAIL]: userData?.email,
        [PAYMENT_FORM_FIELDS.TRANSITION_TYPE]:
          PAYMENT_CONSTANTS.TRANSITION_TYPE,
        [PAYMENT_FORM_FIELDS.SUBMIT]: PAYMENT_FORM_VALUES.SUBMIT,
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      const submitButton = document.createElement("input");
      submitButton.type = "submit";
      submitButton.style.display = "none";
      form.appendChild(submitButton);

      const handleFocus = () => {
        queryClient.invalidateQueries({
          queryKey: ["fbo-unpaid-invoices"],
        });
        setSelectedInvoices([]);
        setCurrentPage(0);
        window.removeEventListener("focus", handleFocus);
      };
      window.addEventListener("focus", handleFocus);

      document.body.appendChild(form);
      submitButton.click();
      form.remove();
    }
  };

  const handleWireReferenceSubmit = () => {
    const paymentData = {
      paymentReferenceNumber: wireReference,
      bankAccountNumber:
        selectedBank === CURRENCY_CODES.BSD
          ? bankConfig?.data?.bsdAccountNumber
          : bankConfig?.data?.usdAccountNumber,
      fboInvoiceIds: selectedInvoices.map((invoice) => invoice.id),
      paymentDate: dayjs(wirePaymentDate).format("YYYY-MM-DD"),
      paymentAmount: wirePaymentAmount,
    };

    wireTransferMutation.mutate(paymentData);
  };
  const handleBankDetailsContinue = () => {
    setOpenBankDetailsDialog(false);
    setOpenWireReferenceDialog(true);
  };
  const wireTransferMutation = useMutation({
    mutationFn: (paymentData) => submitWireTransferPayment(paymentData),
    onSuccess: () => {
      setOpenWireReferenceDialog(false);
      setWireReference(EMPTY_STRING);
      setWirePaymentAmount(0);
      setWirePaymentDate(null);
      setSelectedBank(CURRENCY_CODES.BSD);
      setSelectedInvoices([]);

      queryClient.invalidateQueries({
        queryKey: ["fbo-unpaid-invoices"],
      });

      setCurrentPage(0);
    },
  });
  const handleInvoiceSelection = (selected) => {
    setSelectedInvoices(selected);
  };

  const handleApplyFilter = (selectType, invoiceNumber) => {
    setFilters({
      invoiceType: selectType,
      invoiceNumber: invoiceNumber,
    });
    // Reset to first page when applying filters
    setCurrentPage(0);
    sessionStorage.setItem("fboPaymentCurrentPage", "0");
    // Clear selected invoices when filters change
    setSelectedInvoices([]);
  };

  const handleClearFilter = () => {
    setFilters({
      invoiceNumber: EMPTY_STRING,
      invoiceType: EMPTY_STRING,
    });
    // Reset to first page when clearing filters
    setCurrentPage(0);
    sessionStorage.setItem("fboPaymentCurrentPage", "0");
    // Clear selected invoices when filters change
    setSelectedInvoices([]);
  };
  return (
    <div>
      <NavigationBar />
      <FBOHeaderBar
        title={"Payments"}
        backContent={
          <div className="navigation-header">
            <Link to={ROUTE_PATHS.FBO_HOME} className="navigation-header-link">
              Home/
            </Link>
            Payments
          </div>
        }
      />

      <div className="payment-due-container">
        <div className="payment-filters-row">
          <FboFilters
            onClickApply={handleApplyFilter}
            onClickClear={handleClearFilter}
            bankConfig={bankConfig}
          />
        </div>
        <PaymentDueTable
          paymentDueData={paymentDueData?.data}
          indexStartFrom={indexStartFrom}
          isLoading={isLoading}
          error={error}
          onSelectionChange={handleInvoiceSelection}
          selectedInvoices={selectedInvoices}
        />
        {paymentDueData?.data?.content &&
          paymentDueData?.data?.content.length > 0 && (
            <div className="payment-due-grand-total-container">
              <div className="payment-due-grand-total-text">
                Grand Total: BSD {calculateSelectedTotal().toFixed(2)}
              </div>
              <div className="payment-helper-text">
                When you click the "Pay" button, you will be redirected to the
                next screen to complete your payment.
              </div>
              <Button
                variant="contained"
                color="approve"
                onClick={handlePayClick}
                endIcon={<ArrowRightOutlined style={{ fontSize: "15px" }} />}
              >
                Pay
              </Button>
            </div>
          )}
        {paymentDueData?.data?.content &&
          paymentDueData?.data?.content.length > 0 && (
            <div className="pagination-container">
              <div className="pagination-content">
                Page {currentPage + 1} of {paymentDueData?.data?.totalPages}
              </div>
              <button
                className="pagination-button"
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
              >
                <CaretLeftOutlined />
              </button>
              <button
                className="pagination-button"
                onClick={handleNextPage}
                disabled={currentPage + 1 === paymentDueData?.data?.totalPages}
              >
                <CaretRightOutlined />
              </button>
            </div>
          )}
      </div>
      <Dialog open={openPaymentDialog} onClose={handleClosePaymentDialog}>
        <DialogContent>
          <div className="payment-options-container">
            <h2>Payment Options</h2>
            <RadioGroup
              defaultValue={PAYMENT_CONSTANTS.DEFAULT_PAYMENT_TYPE}
              value={selectedPaymentType}
              onChange={(e) => setSelectedPaymentType(e.target.value)}
            >
              <FormControlLabel
                value={PAYMENT_CONSTANTS.PAYMENT_TYPES.WIRE}
                control={<Radio />}
                label={PAYMENT_CONSTANTS.PAYMENT_LABELS.WIRE}
              />
              <FormControlLabel
                value={PAYMENT_CONSTANTS.PAYMENT_TYPES.CREDIT}
                control={<Radio />}
                label={PAYMENT_CONSTANTS.PAYMENT_LABELS.CREDIT}
              />
            </RadioGroup>
            <Button
              variant="contained"
              fullWidth
              onClick={handlePaymentSubmit}
              sx={{ mt: 2 }}
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openWireReferenceDialog}
        onClose={() => setOpenWireReferenceDialog(false)}
      >
        <DialogContent>
          <div className="wire-reference-container">
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
              {PAYMENT_DIALOG.WIRE_REFERENCE.TITLE}
            </h2>
            <div>
              {selectedBank === CURRENCY_CODES.BSD
                ? bankConfig?.data?.bsdBankName
                : bankConfig?.data?.usdBeneficiaryBank}
            </div>
            <TextField
              fullWidth
              label="Payment Reference Number"
              value={wireReference}
              onChange={(e) => setWireReference(e.target.value)}
              margin="normal"
              placeholder={PAYMENT_DIALOG.WIRE_REFERENCE.PLACEHOLDER}
            />
            <TextField
              fullWidth
              label="Payment Amount (BSD)"
              type="number"
              inputProps={{ min: 0 }}
              value={wirePaymentAmount}
              onChange={(e) => setWirePaymentAmount(e.target.value)}
              margin="normal"
              placeholder={PAYMENT_DIALOG.WIRE_REFERENCE.PAYMENT_PLACEHOLDER}
            />
            <div className="wire-reference-datepicker-container">
              <DateLocalizationProvider>
                <MobileDatePicker
                  className="wire-reference-datepicker"
                  label="Payment Date"
                  format={DATE_FORMAT}
                  maxDate={dayjs()}
                  value={wirePaymentDate}
                  onChange={(newValue) => setWirePaymentDate(newValue)}
                />
              </DateLocalizationProvider>
            </div>

            {wireTransferMutation.error && (
              <p className="error-text">
                Error submitting payment: {wireTransferMutation.error.message}
              </p>
            )}
            <p className="wire-reference-helper-text">
              {PAYMENT_DIALOG.WIRE_REFERENCE.HELPER_TEXT}{" "}
              <span
                className="wire-reference-link"
                onClick={() => {
                  setOpenWireReferenceDialog(false);
                  setOpenPaymentDialog(true);
                }}
              >
                {PAYMENT_DIALOG.WIRE_REFERENCE.LINK_TEXT}
              </span>{" "}
              {PAYMENT_DIALOG.WIRE_REFERENCE.LINK_SUFFIX}
            </p>
            <Button
              variant="contained"
              fullWidth
              onClick={handleWireReferenceSubmit}
              sx={{ mt: 2 }}
              disabled={
                !wireReference.trim() ||
                wirePaymentAmount <= 0 ||
                wirePaymentDate === null ||
                wireTransferMutation.isPending
              }
            >
              {wireTransferMutation.isPending
                ? "Processing..."
                : PAYMENT_DIALOG.WIRE_REFERENCE.SAVE_BUTTON}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openBankDetailsDialog}
        onClose={() => setOpenBankDetailsDialog(false)}
      >
        <DialogContent>
          <div className="bank-details-container">
            <h2>Choose Bank</h2>
            {bankConfigLoading ? (
              <CircularProgress />
            ) : (
              <RadioGroup
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
              >
                <FormControlLabel
                  value={CURRENCY_CODES.BSD}
                  control={<Radio />}
                  label={bankConfig?.data?.bsdBankName || "BSD Bank"}
                />
                <FormControlLabel
                  value={CURRENCY_CODES.USD}
                  control={<Radio />}
                  label={bankConfig?.data?.usdBeneficiaryBank || "USD Bank"}
                />
              </RadioGroup>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handleBankDetailsContinue}
              sx={{ mt: 2 }}
              disabled={bankConfigLoading}
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {snackbar.open && (
        <SnackbarAlert type="error">{snackbar.message}</SnackbarAlert>
      )}
    </div>
  );
};

export default FboPaymentScreen;
